import { toast } from "react-toastify";
import { io, Socket } from "socket.io-client";

// Singleton: Uma única instância de socket para toda a aplicação
let socket: Socket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let blockedToken: string | null = null;
let lastSocketErrorKey: string | null = null;
let lastSocketErrorAt = 0;

function getStoredToken(): string | null {
  try {
    const parsed = JSON.parse(localStorage.getItem("token") || "{}");
    return parsed?.token || null;
  } catch {
    return null;
  }
}

function applySocketAuth(target: Socket) {
  const token = getStoredToken();
  target.auth = token ? { token } : {};
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function canConnectWithStoredToken() {
  const token = getStoredToken();
  if (!token) {
    blockedToken = null;
    return false;
  }

  if (blockedToken && blockedToken !== token) {
    blockedToken = null;
  }

  return blockedToken !== token;
}

function showSocketErrorOnce(message: string, code?: string) {
  const key = `${code || "unknown"}:${message}`;
  const now = Date.now();

  if (lastSocketErrorKey === key && now - lastSocketErrorAt < 5000) {
    return;
  }

  lastSocketErrorKey = key;
  lastSocketErrorAt = now;
  toast.error(message);
}

export function getSocket(): Socket {
  if (!socket) {
    const token = getStoredToken();
    socket = io(import.meta.env.VITE_REACT_API_URL, {
      // Configurações para reconexão robusta
      transports: ["websocket"],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: Infinity, // Tentar reconectar indefinidamente
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      autoConnect: Boolean(token),
      forceNew: false,
      auth: token ? { token } : {},
    });

    socket.on("connect", () => {
      clearReconnectTimer();
      blockedToken = null;
    });

    socket.on("disconnect", (reason) => {
      if (
        (reason === "io server disconnect" || reason === "transport close") &&
        canConnectWithStoredToken()
      ) {
        reconnectTimer = setTimeout(() => {
          if (socket && !socket.connected && canConnectWithStoredToken()) {
            applySocketAuth(socket);
            socket.connect();
          }
        }, 2000);
      }
    });

    socket.on(
      "socket:error",
      (payload: { code?: string; message?: string } | null) => {
        const message =
          payload?.message ||
          "Falha na conexão em tempo real. Recarregue a página.";

        if (payload?.code === "UNAUTHORIZED_SOCKET") {
          blockedToken = getStoredToken();
          clearReconnectTimer();
          if (socket?.connected) {
            socket.disconnect();
          }
        }

        showSocketErrorOnce(message, payload?.code);
      },
    );
  } else {
    applySocketAuth(socket);
  }

  if (!socket.connected && canConnectWithStoredToken()) {
    applySocketAuth(socket);
    socket.connect();
  }

  return socket;
}

// Função para desconectar o socket (usar no logout)
export function disconnectSocket(): void {
  if (socket) {
    clearReconnectTimer();
    blockedToken = null;
    lastSocketErrorKey = null;
    lastSocketErrorAt = 0;
    socket.disconnect();
    socket = null;
  }
}

export default getSocket;
